class CollectionVisiblePagination extends HTMLElement {
  static maxConcurrentRequests = 4;

  static requiresCompaction(serverPages, serverProducts, visibleProducts) {
    return serverPages > 1 && serverProducts !== visibleProducts;
  }

  static compactPages(pages, pageSize, requestedPage) {
    const products = pages.flat();
    const totalPages = products.length === 0 ? 0 : Math.ceil(products.length / pageSize);
    const currentPage = totalPages === 0 ? 1 : Math.min(Math.max(requestedPage, 1), totalPages);
    const start = (currentPage - 1) * pageSize;

    return {
      currentPage,
      products: products.slice(start, start + pageSize),
      totalPages,
      totalProducts: products.length,
    };
  }

  static paginationTokens(totalPages, currentPage) {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);

    const visiblePages = new Set([
      1,
      2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      totalPages - 1,
      totalPages,
    ].filter((page) => page >= 1 && page <= totalPages));
    const sortedPages = Array.from(visiblePages).sort((left, right) => left - right);

    return sortedPages.flatMap((page, index) => {
      if (index === 0 || page === sortedPages[index - 1] + 1) return [page];
      return ['ellipsis', page];
    });
  }

  static pageUrl(href, page, sectionId = '') {
    const url = new URL(href, window.location.origin);
    url.hash = '';
    url.searchParams.delete('section_id');
    if (page <= 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    if (sectionId) url.searchParams.set('section_id', sectionId);
    return url;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abortController = new AbortController();
    this.compact()
      .catch((error) => {
        if (error.name !== 'AbortError') console.error('Visible collection pagination failed', error);
      })
      .finally(() => this.removeAttribute('aria-busy'));
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  productMarkup(root = this) {
    const grid = root.querySelector('#product-grid');
    if (!grid || grid.tagName !== 'UL') return [];
    return Array.from(grid.children)
      .filter((item) => item.tagName === 'LI' && item.classList.contains('grid__item'))
      .map((item) => item.outerHTML);
  }

  async fetchPage(page) {
    const url = CollectionVisiblePagination.pageUrl(window.location.href, page, this.dataset.sectionId);
    const response = await fetch(url, {
      headers: { Accept: 'text/html' },
      signal: this.abortController.signal,
    });
    if (!response.ok) throw new Error(`Collection page ${page} returned ${response.status}`);

    const documentFromResponse = new DOMParser().parseFromString(await response.text(), 'text/html');
    const source = documentFromResponse.querySelector('#ProductGridContainer collection-visible-pagination');
    if (!source) throw new Error(`Collection page ${page} did not contain the visible grid`);
    return this.productMarkup(source);
  }

  async fetchMissingPages(serverPages, currentPage) {
    const pages = Array.from({ length: serverPages }, () => []);
    pages[currentPage - 1] = this.productMarkup();
    const pendingPages = Array.from({ length: serverPages }, (_, index) => index + 1)
      .filter((page) => page !== currentPage);
    let nextIndex = 0;

    const worker = async () => {
      while (nextIndex < pendingPages.length) {
        const page = pendingPages[nextIndex];
        nextIndex += 1;
        pages[page - 1] = await this.fetchPage(page);
      }
    };
    const workerCount = Math.min(CollectionVisiblePagination.maxConcurrentRequests, pendingPages.length);
    await Promise.all(Array.from({ length: workerCount }, worker));
    return pages;
  }

  renderProducts(productMarkup) {
    const grid = this.querySelector('#product-grid');
    if (!grid || grid.tagName !== 'UL') return;
    grid.innerHTML = productMarkup.join('');
    grid.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  paginationLabel(page) {
    return (this.dataset.paginationPageLabel || 'Side __PAGE__').replace('__PAGE__', String(page));
  }

  paginationLink(page, { relation = '' } = {}) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = CollectionVisiblePagination.pageUrl(window.location.href, page).toString();
    const arrowDirection = relation === 'previous' ? 'next' : relation === 'next' ? 'prev' : '';
    link.className = arrowDirection
      ? `pagination__item pagination__item--${arrowDirection} pagination__item-arrow link motion-reduce`
      : 'pagination__item link';
    link.setAttribute(
      'aria-label',
      relation === 'previous'
        ? this.dataset.paginationPreviousLabel || 'Forrige side'
        : relation === 'next'
          ? this.dataset.paginationNextLabel || 'Næste side'
          : this.paginationLabel(page),
    );

    if (relation) {
      const icon = document.createElement('span');
      icon.className = 'svg-wrapper';
      icon.innerHTML = this.querySelector('template[data-pagination-caret]')?.innerHTML || '';
      link.append(icon);
    } else {
      link.textContent = String(page);
    }
    item.append(link);
    return item;
  }

  renderPagination(totalPages, currentPage) {
    this.querySelector('.pagination-wrapper')?.remove();
    if (totalPages <= 1) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'pagination-wrapper';
    wrapper.dataset.page = String(currentPage);
    const navigation = document.createElement('nav');
    navigation.className = 'pagination';
    navigation.setAttribute('role', 'navigation');
    navigation.setAttribute('aria-label', this.dataset.paginationLabel || 'Pagination');
    const list = document.createElement('ul');
    list.className = 'pagination__list list-unstyled';
    list.setAttribute('role', 'list');

    if (currentPage > 1) list.append(this.paginationLink(currentPage - 1, { relation: 'previous' }));
    for (const token of CollectionVisiblePagination.paginationTokens(totalPages, currentPage)) {
      if (token === 'ellipsis') {
        const item = document.createElement('li');
        const ellipsis = document.createElement('span');
        ellipsis.className = 'pagination__item';
        ellipsis.textContent = '…';
        item.append(ellipsis);
        list.append(item);
      } else if (token === currentPage) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.setAttribute('role', 'link');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('aria-current', 'page');
        link.setAttribute('aria-label', this.paginationLabel(token));
        link.className = 'pagination__item pagination__item--current light';
        link.textContent = String(token);
        item.append(link);
        list.append(item);
      } else {
        list.append(this.paginationLink(token));
      }
    }
    if (currentPage < totalPages) list.append(this.paginationLink(currentPage + 1, { relation: 'next' }));

    navigation.append(list);
    wrapper.append(navigation);
    this.append(wrapper);
  }

  async compact() {
    const serverPages = Number.parseInt(this.dataset.serverPages, 10) || 1;
    const requestedServerPage = Number.parseInt(this.dataset.currentPage, 10) || 1;
    const serverCurrentPage = Math.min(Math.max(requestedServerPage, 1), serverPages);
    const pageSize = Number.parseInt(this.dataset.pageSize, 10) || 16;
    const serverProducts = Number.parseInt(this.dataset.serverProducts, 10) || 0;
    const visibleProducts = this.productMarkup().length;
    if (!CollectionVisiblePagination.requiresCompaction(serverPages, serverProducts, visibleProducts)) return;

    this.setAttribute('aria-busy', 'true');
    const pages = await this.fetchMissingPages(serverPages, serverCurrentPage);
    if (!this.isConnected) return;

    const compacted = CollectionVisiblePagination.compactPages(pages, pageSize, serverCurrentPage);
    this.renderProducts(compacted.products);
    this.renderPagination(compacted.totalPages, compacted.currentPage);
    this.dataset.visibleProducts = String(compacted.totalProducts);
    this.dataset.visiblePages = String(compacted.totalPages);
  }
}

if (!customElements.get('collection-visible-pagination')) {
  customElements.define('collection-visible-pagination', CollectionVisiblePagination);
}
